import React, { useState, useRef, useEffect } from 'react';
import Icon from '../shared/Icon';
import './QuickUploadModal.css';
import { apiService } from '../../../services/api';
import { combineUploadOutcomes, notifyRejectedUploads, uploadSingleFile } from '../../../utils/uploadOutcome';

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function QuickUploadModal({ isOpen, initialFiles, onClose, onShowNotification, onPublishSuccess }) {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [datasetName, setDatasetName] = useState('');
  const [description, setDescription] = useState('');
  const [groupAsOne, setGroupAsOne] = useState(true);
  const [fileDatasetNames, setFileDatasetNames] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCheckingNames, setIsCheckingNames] = useState(false);
  const addMoreInputRef = useRef(null);
  // Modules dropdown — null = working dataset (default, current behavior).
  // When a module is selected, publish takes the per-file fan-out path:
  // backend creates one dataset per file under the module.
  const [enabledModules, setEnabledModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const incoming = initialFiles ? Array.from(initialFiles) : [];
      const baseNames = incoming.map((f) => f.name.replace(/\.[^/.]+$/, ''));
      setFiles(incoming);
      setFileDatasetNames(baseNames);
      setStep(1);
      setDatasetName(baseNames[0] || '');
      setDescription('');
      setGroupAsOne(true);
      setIsPublishing(false);
      setSelectedModuleId(null);
      // Enabled modules for this org's dropdown. Silent on error — the
      // modal still works as a plain dataset upload if the call fails.
      apiService
        .getMyModules()
        .then((resp) => setEnabledModules(resp?.modules || []))
        .catch(() => setEnabledModules([]));
    }
  }, [isOpen, initialFiles]);

  if (!isOpen) return null;

  const handleAddMoreClick = () => {
    if (addMoreInputRef.current) addMoreInputRef.current.click();
  };

  const handleAddMoreChange = (e) => {
    const newFiles = e.target.files ? Array.from(e.target.files) : [];
    if (newFiles.length === 0) return;
    setFiles((prev) => [...prev, ...newFiles]);
    setFileDatasetNames((prev) => [...prev, ...newFiles.map((f) => f.name.replace(/\.[^/.]+$/, ''))]);
    e.target.value = '';
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileDatasetNames((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFileNameChange = (idx, value) => {
    setFileDatasetNames((prev) => prev.map((n, i) => (i === idx ? value : n)));
  };

  const findDuplicateName = async (candidateNames) => {
    const uniqueCandidates = [...new Set(candidateNames.map((n) => n.trim().toLowerCase()))];
    for (const candidate of uniqueCandidates) {
      const response = await apiService.getDatasets({ search: candidate, page_size: 50 });
      const items = response?.items || [];
      const hit = items.find((d) => (d.name || '').trim().toLowerCase() === candidate);
      if (hit) return hit.name;
    }
    return null;
  };

  const handleNext = async () => {
    if (files.length === 0) {
      if (onShowNotification) onShowNotification('Please attach at least one file.', 'error');
      return;
    }
    // Module path delegates naming + collision resolution to the backend
    // fan-out endpoint. Skip the duplicate-name check entirely.
    if (selectedModuleId) {
      setStep(2);
      return;
    }
    let candidateNames;
    if (groupAsOne) {
      if (!datasetName.trim()) {
        if (onShowNotification) onShowNotification('Please enter a dataset name.', 'error');
        return;
      }
      candidateNames = [datasetName];
    } else {
      if (fileDatasetNames.some((n) => !n?.trim())) {
        if (onShowNotification) onShowNotification('All file dataset names must be filled in.', 'error');
        return;
      }
      candidateNames = fileDatasetNames;
    }

    setIsCheckingNames(true);
    try {
      const duplicate = await findDuplicateName(candidateNames);
      if (duplicate) {
        if (onShowNotification) {
          onShowNotification(`A dataset named "${duplicate}" already exists. Please choose a different name.`, 'error');
        }
        return;
      }
      setStep(2);
    } catch (error) {
      const message = error?.responseData?.detail || error?.message || 'Failed to verify dataset name.';
      if (onShowNotification) {
        onShowNotification(typeof message === 'string' ? message : 'Failed to verify dataset name.', 'error');
      }
    } finally {
      setIsCheckingNames(false);
    }
  };

  const handleBack = () => setStep(1);

  const handleClose = () => {
    if (isPublishing) return;
    onClose();
  };

  const handlePublish = async () => {
    if (files.length === 0) {
      if (onShowNotification) onShowNotification('Please attach at least one file.', 'error');
      return;
    }
    setIsPublishing(true);
    try {
      // Module path — delegate everything (dataset rows, naming, ingestion)
      // to the per-file fan-out endpoint. Partial-success per file.
      if (selectedModuleId) {
        const resp = await apiService.uploadFilesToModule(selectedModuleId, files);
        const results = resp?.results || [];
        const succeeded = results.filter((r) => !r.error);
        const failed = results.filter((r) => r.error);
        if (onShowNotification) {
          if (failed.length === 0) {
            onShowNotification(
              `${succeeded.length} file${succeeded.length === 1 ? '' : 's'} uploaded to the library`,
              'success',
            );
          } else {
            onShowNotification(
              `${succeeded.length}/${results.length} uploaded; ${failed.length} failed: ${failed.map((r) => r.file_name).join(', ')}`,
              failed.length === results.length ? 'error' : 'alert',
            );
          }
        }
        onClose();
        if (onPublishSuccess && succeeded.length > 0) onPublishSuccess(succeeded[0].dataset_id);
        return;
      }
      const createdIds = [];
      const outcomes = [];
      if (groupAsOne) {
        const name = datasetName.trim();
        const created = await apiService.createDataset({
          name,
          description: description.trim() || name,
          prompt: '',
        });
        if (!created?.id) throw new Error('Dataset creation returned no id');
        createdIds.push(created.id);
        // PRD-1628: a 207 kept the good files; the rejected ones are named below.
        outcomes.push(await apiService.uploadFiles(created.id, files, 'local', '', ''));
        await apiService.extractFields(created.id);
      } else {
        for (let i = 0; i < files.length; i++) {
          const name = (fileDatasetNames[i] || files[i].name).trim();
          const created = await apiService.createDataset({
            name,
            description: name,
            prompt: '',
          });
          if (!created?.id) throw new Error('Dataset creation returned no id');
          createdIds.push(created.id);
          // PRD-1628: a rejected file doesn't stop the files after it, and a
          // dataset whose only file was rejected has nothing to index.
          const outcome = await uploadSingleFile(files[i], () => apiService.uploadFiles(created.id, [files[i]], 'local', '', ''));
          outcomes.push(outcome);
          if (outcome.uploaded.length > 0) await apiService.extractFields(created.id);
        }
      }
      // A rejected-files notice already says what went through; otherwise, as before.
      const notifiedRejected = notifyRejectedUploads(combineUploadOutcomes(outcomes), onShowNotification);
      if (!notifiedRejected && onShowNotification) {
        onShowNotification(
          createdIds.length === 1
            ? 'Dataset indexing has started'
            : `${createdIds.length} datasets indexing has started`,
          'success'
        );
      }
      onClose();
      if (onPublishSuccess) onPublishSuccess(createdIds[0]);
    } catch (error) {
      const message =
        error?.responseData?.detail ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to publish dataset. Please try again.';
      if (onShowNotification) {
        onShowNotification(typeof message === 'string' ? message : 'Failed to publish dataset.', 'error');
      }
      setIsPublishing(false);
    }
  };

  return (
    <div className="quick-upload-overlay">
      <div className="quick-upload-modal">
        <div className="quick-upload-header">
          <h2 className="quick-upload-title">New dataset</h2>
          <button type="button" className="quick-upload-close" onClick={handleClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        <div className="quick-upload-divider" />

        {step === 1 ? (
          <div className="quick-upload-body">
            <h3 className="quick-upload-section-title">Add data</h3>
            <p className="quick-upload-section-subtitle">
              Choose a file and give this dataset a name. Everything else can be done after publish.
            </p>

            <div className="quick-upload-file-card">
              <div className="quick-upload-file-card-header">
                <span className="quick-upload-file-count">
                  {files.length} {files.length === 1 ? 'FILE ATTACHED' : 'FILES ATTACHED'}
                </span>
                <button type="button" className="quick-upload-add-more" onClick={handleAddMoreClick}>
                  + Add more
                </button>
              </div>
              {files.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="quick-upload-file-row">
                  <Icon name="description" className="quick-upload-file-icon" size={18} />
                  <span className="quick-upload-file-name">{file.name}</span>
                  <span className="quick-upload-file-size">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="quick-upload-file-remove"
                    onClick={() => handleRemoveFile(idx)}
                    aria-label="Remove file"
                  >
                    <Icon name="close" />
                  </button>
                </div>
              ))}
            </div>

            {/* Radio choice between default dataset and module fan-out. */}
            {enabledModules.length > 0 && (
              <>
                <label className="resui-radio">
                  <input
                    type="radio"
                    className="resui-radio-input"
                    name="quick-upload-target"
                    checked={!selectedModuleId}
                    onChange={() => setSelectedModuleId(null)}
                  />
                  <span className="resui-radio-mark" />
                  <span className="resui-radio-label">Default dataset</span>
                </label>
                <label className="resui-radio">
                  <input
                    type="radio"
                    className="resui-radio-input"
                    name="quick-upload-target"
                    checked={!!selectedModuleId}
                    onChange={() => setSelectedModuleId(enabledModules[0]?.module_id || null)}
                  />
                  <span className="resui-radio-mark" />
                  <span className="resui-radio-label">Add to a module</span>
                </label>
                {selectedModuleId && (
                  <div className="quick-upload-module-select">
                    <label className="quick-upload-label">Module Name</label>
                    <select
                      className="resui-select"
                      value={selectedModuleId}
                      onChange={(e) => setSelectedModuleId(e.target.value)}
                    >
                      {enabledModules.map((m) => (
                        <option key={m.module_id} value={m.module_id}>{m.name}</option>
                      ))}
                    </select>
                    <p className="quick-upload-helper">
                      Each file becomes its own dataset under this module. Names
                      are derived from the file name.
                    </p>
                  </div>
                )}
              </>
            )}

            {files.length > 1 && !selectedModuleId && (
              <label className="quick-upload-group-toggle resui-checkbox">
                <input
                  type="checkbox"
                  className="resui-checkbox-input"
                  checked={groupAsOne}
                  onChange={(e) => setGroupAsOne(e.target.checked)}
                />
                <span className="resui-checkbox-mark" />
                <span className="resui-checkbox-label">Group all files as one dataset</span>
              </label>
            )}

            {selectedModuleId ? null : groupAsOne ? (
              <>
                <h3 className="quick-upload-section-title">Name this dataset</h3>
                <label className="quick-upload-label">
                  Dataset Name<span className="quick-upload-required">*</span>
                </label>
                <input
                  type="text"
                  className="resui-input"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  placeholder="Enter dataset name"
                />
                <p className="quick-upload-helper">This name appears in Datasets and Ask ResAI.</p>

                <label className="quick-upload-label">Description (Optional)</label>
                <textarea
                  className="resui-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional note for your team..."
                  rows={3}
                />
              </>
            ) : (
              <>
                <h3 className="quick-upload-section-title">Edit names — one dataset per file</h3>
                {files.map((file, idx) => (
                  <div key={`name-${file.name}-${idx}`} className="quick-upload-name-row-horizontal">
                    <Icon name="description" className="quick-upload-file-icon" size={18} />
                    <span className="quick-upload-name-filename">{file.name}</span>
                    <input
                      type="text"
                      className="resui-input quick-upload-name-input"
                      value={fileDatasetNames[idx] || ''}
                      onChange={(e) => handleFileNameChange(idx, e.target.value)}
                      placeholder="Dataset name"
                    />
                  </div>
                ))}

                <label className="quick-upload-label">Description (Optional)</label>
                <textarea
                  className="resui-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional note for your team..."
                  rows={3}
                />
              </>
            )}
          </div>
        ) : (
          <div className="quick-upload-body">
            <h3 className="quick-upload-section-title">Publish</h3>
            <p className="quick-upload-section-subtitle">
              Confirm what's about to be ingested and publish to start indexing.
            </p>

            <div className="quick-upload-file-card quick-upload-file-card--publish">
              <div className="quick-upload-file-card-header quick-upload-file-card-header--publish">
                <span className="quick-upload-publish-title">Files to ingest</span>
              </div>
              {files.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="quick-upload-publish-row">
                  <div className="quick-upload-publish-file">
                    <div className="quick-upload-file-name">{file.name}</div>
                    <div className="quick-upload-publish-meta">
                      {file.type || 'application/octet-stream'} &middot; {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="quick-upload-divider" />

        <div className="quick-upload-footer">
          {step === 1 ? (
            <button
              type="button"
              className="quick-upload-btn-primary"
              onClick={handleNext}
              disabled={isCheckingNames}
            >
              NEXT
            </button>
          ) : (
            <>
              <button
                type="button"
                className="quick-upload-btn-secondary"
                onClick={handleBack}
                disabled={isPublishing}
              >
                BACK
              </button>
              <button
                type="button"
                className="quick-upload-btn-primary"
                onClick={handlePublish}
                disabled={isPublishing || files.length === 0}
              >
                {isPublishing ? 'PUBLISHING...' : 'PUBLISH AND START INDEXING'}
              </button>
            </>
          )}
        </div>

        <input
          ref={addMoreInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleAddMoreChange}
        />
      </div>
    </div>
  );
}

export default QuickUploadModal;
